import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, CalendarRange, Inbox, BookOpen, GraduationCap, Megaphone,
    Users, School, ChevronsUpDown, User, Settings, LogOut, ArrowRightLeft,
    PanelLeftClose, PanelLeftOpen, CalendarCheck2, Receipt, KeyRound, Send, Eye,
    ClipboardList, Layers, MessageSquare, Activity, Footprints,
} from 'lucide-react';
import { Popover, Menu, MenuItem, Tooltip } from '@bnvsrnd/brand-assets';
import { AuthContext } from '../../contexts/AuthContext';
import adminLogo from '../../assets/images/duotutor_admin_logo.svg';
import './AdminLayout.css';

/**
 * 관리자 포털 공통 셸 (좌측 사이드바 + 상단 헤더, 멘토 포털과 동일한 디자인 언어).
 * - 어드민은 자체 사이드바를 쓰므로 메인 상단바(NavigationBar)는 App.js 에서 숨긴다(standalone).
 * - 사이드바 하단의 "멘토 포털 →" 로 멘토 모드로 전환(전체화면). 멘토 포털에서는 "← 관리자" 로 복귀.
 */
// admin과 staff는 관리자 화면에서 완전히 동일한 메뉴를 본다 — adminOnly 구분을 두지 않는다.
const NAV_SECTIONS = [
    {
        items: [
            { to: '/admin/overview', label: '운영 현황', icon: <LayoutDashboard size="1em" /> },
            { to: '/admin/reflections-live', label: '소감문 실시간', icon: <Activity size="1em" /> },
            { to: '/admin/visitors', label: '방문자 분석', icon: <Footprints size="1em" /> },
        ],
    },
    {
        label: '수업운영',
        items: [
            { to: '/admin/class-operations', label: '수업 일정', icon: <CalendarRange size="1em" /> },
            { to: '/admin/class-inquiry', label: '수업 문의', icon: <Inbox size="1em" /> },
            { to: '/admin/curriculum', label: '커리큘럼', icon: <BookOpen size="1em" /> },
            { to: '/admin/instructors', label: '멘토 관리', icon: <GraduationCap size="1em" /> },
            { to: '/admin/instructor-overview', label: '멘토 현황', icon: <Eye size="1em" /> },
            { to: '/admin/instructor-recruitment', label: '멘토 모집', icon: <Megaphone size="1em" /> },
            { to: '/admin/instructor-history', label: '듀오멘토 출강', icon: <CalendarCheck2 size="1em" /> },
            { to: '/admin/instructor-settlement', label: '듀오멘토 강의료', icon: <Receipt size="1em" /> },
            { to: '/admin/monthly-settlement', label: '월별 정산 취합', icon: <Send size="1em" /> },
        ],
    },
    {
        label: '시스템',
        items: [
            { to: '/admin/users', label: '사용자', icon: <Users size="1em" /> },
            { to: '/admin/schools', label: '학교 관리', icon: <School size="1em" /> },
            { to: '/admin/classes', label: '반 관리', icon: <Layers size="1em" /> },
            { to: '/admin/board', label: '게시판 관리', icon: <MessageSquare size="1em" /> },
            { to: '/admin/cost', label: 'AI 비용', icon: <ClipboardList size="1em" /> },
        ],
    },
];

function AdminLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useContext(AuthContext);
    const [mobileOpen, setMobileOpen] = useState(false);
    // 접힘 상태 — 페이지 이동/새로고침에도 유지(localStorage). 어드민·멘토 공통 키.
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
    });
    const toggleCollapsed = () => setCollapsed((v) => {
        const next = !v;
        try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
        return next;
    });

    // 사이드바 하단 프로필 팝오버 — 스크롤 중 위치가 어긋나 보이는 문제를 막기 위해
    // controlled로 열고, 스크롤이 나면 닫는다(자세한 이유는 아래 JSX 주석 참조).
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    useEffect(() => {
        if (!profileMenuOpen) return undefined;
        const handleScroll = () => setProfileMenuOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [profileMenuOpen]);

    // 활성 메뉴: 정확히 일치하거나 하위 경로(예: /admin/class-operations/:id)
    const isActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);

    const closeMobile = () => setMobileOpen(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const displayName = (user && user.name) || '관리자';
    // admin과 staff는 관리자 화면에서 완전히 동일한 기능을 쓴다 — 유일한 차이는 아래 "멘토
    // 포털" 전환 링크뿐이다(admin은 실제 멘토가 아니라 그 링크가 의미 없어 숨긴다).
    const isFullAdmin = !!(user && user.isAdmin);
    const homeLink = '/admin/overview';

    // 멘토 포털 전환 링크 — 접힘 상태에선 DS Tooltip(우측)으로 감싼다
    const switchLink = (
        <Link to="/instructor/dashboard" className="admin-nav-link" onClick={closeMobile}>
            <span className="admin-nav-icon"><ArrowRightLeft size="1em" /></span>
            <span>멘토 포털</span>
        </Link>
    );

    return (
        <div className="admin-shell">
            {/* 모바일 사이드바 백드롭 */}
            <div
                className={`admin-sidebar-backdrop ${mobileOpen ? 'open' : ''}`}
                onClick={closeMobile}
            />

            {/* 사이드바 */}
            <nav className={`admin-sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
                <div className="admin-sidebar-brand">
                    <Link to={homeLink} className="admin-sidebar-logo" onClick={closeMobile}>
                        <img src={adminLogo} alt="DuoTutor 관리자" style={{ height: '50px', width: 'auto' }} />
                    </Link>
                    <button
                        type="button"
                        className="admin-collapse-toggle"
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
                    >
                        {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
                    </button>
                </div>

                <div className="admin-sidebar-nav">
                    {NAV_SECTIONS.map((section, si) => (
                        <div key={section.label || `sec-${si}`}>
                            {section.label && (
                                <div className={`admin-nav-section-label ${si === 0 ? 'first' : ''}`}>
                                    {section.label}
                                </div>
                            )}
                            {section.items.map((item) => {
                                const link = (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        className={`admin-nav-link ${isActive(item.to) ? 'active' : ''}`}
                                        onClick={closeMobile}
                                    >
                                        <span className="admin-nav-icon">{item.icon}</span>
                                        <span>{item.label}</span>
                                    </Link>
                                );
                                // 접힘 상태: 라벨이 안 보이므로 DS Tooltip(우측)으로 보완
                                return collapsed
                                    ? <Tooltip key={item.to} content={item.label} position="right">{link}</Tooltip>
                                    : link;
                            })}
                        </div>
                    ))}
                </div>

                {/* 멘토 포털 전환 — staff 본인의 실제 멘토 포털로 돌아가는 링크. admin은 실제 멘토가
                    아니라 이 링크가 의미 없으므로(대신 위 사이드바의 "멘토 현황"이 admin용 조망 화면) 숨긴다. */}
                {!isFullAdmin && (
                    <div className="admin-sidebar-switch">
                        {collapsed
                            ? <Tooltip content="멘토 포털" position="right">{switchLink}</Tooltip>
                            : switchLink}
                    </div>
                )}

                {/* 하단 — 프로필 (구분선 아래, 오른쪽으로 열리는 드롭다운).
                    @bnvsrnd/brand-assets의 Popover는 열려있는 동안 스크롤을 감지해 위치를
                    다시 계산하지 않아서, 사이드바가 position: sticky로 스크롤에 따라 화면상
                    위치가 바뀌면 패널이 트리거를 따라오지 못하고 어긋나 보인다 — 스크롤이
                    나면 패널을 닫아서(controlled open) 어긋난 채로 떠있는 상태를 없앤다.

                    또한 placement="right"는 트리거 세로 중앙 기준으로만 좌표를 잡고 뷰포트
                    하단 클램프가 없어서(left/right 배치 공통 버그) 패널 아래쪽이 살짝
                    잘렸다. .profile-popover-panel 클래스로 position을 fixed로 강제하고
                    bottom을 고정값으로 줘서 항상 화면 하단에서 일정 간격 띄운 채로 표시되게
                    한다(AdminLayout.css 참조). */}
                <div className="admin-sidebar-foot">
                    <Popover
                        placement="right"
                        open={profileMenuOpen}
                        onOpenChange={setProfileMenuOpen}
                        className="profile-popover-panel"
                        trigger={
                            <button type="button" className="admin-profile">
                                <span className="admin-profile-avatar"><User size={18} /></span>
                                <span className="admin-profile-info">
                                    <span className="admin-profile-name">{displayName}</span>
                                    <span className="admin-profile-role">관리자</span>
                                </span>
                                <ChevronsUpDown size={16} className="admin-profile-chevron" />
                            </button>
                        }
                    >
                        <Menu>
                            <MenuItem
                                icon={<Settings size={16} />}
                                onClick={() => navigate('/admin/account')}
                            >
                                계정 관리
                            </MenuItem>
                            <MenuItem
                                icon={<KeyRound size={16} />}
                                onClick={() => navigate('/admin/mcp-tokens')}
                            >
                                MCP
                            </MenuItem>
                            <MenuItem
                                type="destructive"
                                icon={<LogOut size={16} />}
                                onClick={handleLogout}
                            >
                                로그아웃
                            </MenuItem>
                        </Menu>
                    </Popover>
                </div>
            </nav>

            {/* 메인 컬럼 (헤더 없음) */}
            <div className="admin-main">
                {/* 모바일 전용: 사이드바 토글 (헤더가 없어 플로팅 버튼으로 제공) */}
                <button
                    type="button"
                    className="admin-mobile-toggle"
                    aria-label="메뉴 열기"
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    ☰
                </button>

                <main className="admin-content">
                    <div className="admin-content-inner">{children}</div>
                </main>
            </div>
        </div>
    );
}

export default AdminLayout;
